<template>
    <div class="container-fluid">
        <div class="row">
            <!-- Sidebar for Form Controls -->
            <div class="col-md-3 sidebar bg-light">
                <div class="info-panel p-3">
                    <h2 class="mb-4">Ensemble Analysis</h2>
                    <p class="text-muted">Analyze and compare median projections and uncertainty ranges from multiple models for selected scenarios.</p>

                    <!-- RCP Scenario Selection -->
                    <div class="form-group mt-3">
                        <label>Select RCP Scenarios</label>
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" id="rcp-all" @change="toggleSelectAllScenarios" :checked="allScenariosSelected">
                            <label class="form-check-label" for="rcp-all">
                                <strong>All Scenarios</strong>
                            </label>
                        </div>
                        <hr>
                        <div v-for="scenario in availableRcpScenarios" :key="scenario" class="form-check">
                            <input class="form-check-input" type="checkbox" :value="scenario" v-model="selectedRcpScenarios" :id="`rcp-${scenario}`">
                            <label class="form-check-label" :for="`rcp-${scenario}`">
                                {{ scenario }}
                            </label>
                        </div>
                    </div>

                    <!-- Parameter Selection -->
                    <div class="form-group mt-3">
                        <label for="parameter-select">Select Climate Indicator</label>
                        <select id="parameter-select" v-model="selectedParameter" class="form-control">
                            <option v-for="(param, index) in parameterNames" :key="index" :value="param">{{ param }}</option>
                        </select>
                    </div>

                    <!-- Date Selection -->
                    <div class="form-group mt-3">
                        <label for="date-select">Select Start Year</label>
                        <Datepicker v-model="selectedStartDate" year-picker auto-apply />
                    </div>

                    <div class="form-group mt-3">
                        <label for="date-select">Select End Year</label>
                        <Datepicker v-model="selectedEndDate" year-picker auto-apply />
                    </div>

                    <!-- Temporal Aggregation -->
                    <div class="form-group mt-3">
                        <label for="temporal-select">Select Temporal Aggregation</label>
                        <select id="temporal-select" v-model="selectedTemporalAggregation" class="form-control">
                            <option>ANNUAL</option>
                            <option>MONTHLY</option>
                            <option>SEASONAL</option>
                        </select>
                    </div>

                    <!-- Spatial Extent -->
                    <div class="mt-4">
                        <button class="btn btn-secondary" @click="showMapModal = true">
                            Select Spatial Extent
                        </button>
                    </div>

                    <!-- Fetch Data Button -->
                    <div class="mt-3">
                        <button class="btn btn-primary" :disabled="selectedRcpScenarios.length === 0 || isLoading" @click="fetchEnsembleData">
                            {{ isLoading ? 'Analyzing...' : 'Run Ensemble Analysis' }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Main Content: Ensemble Chart -->
            <div class="col-md-9">
                <div v-if="isLoading" class="loading-indicator mt-2">
                    <div class="spinner"></div>
                    <p>Fetching and analyzing ensemble data, please wait...</p>
                </div>
                <div v-if="Object.keys(ensemblePlotData).length > 0">
                    <div id="ensembleChartContainer" class="mt-4">
                        <h5>Ensemble Climate Projection for {{ selectedRcpScenarios.join(', ') }}</h5>
                        <p>Showing Median and Interquartile Range (25th-75th percentile) for each scenario.</p>
                        <div id="ensembleChart" style="width: 100%; height: 500px;"></div>
                    </div>
                </div>
                <div v-else-if="!isLoading">
                    <div class="placeholder-container">
                        <p class="placeholder-text">Please select scenarios and run the analysis.</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal for Mapbox Map -->
        <div v-if="showMapModal" class="modal-overlay">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Select Spatial Extent</h5>
                    <button type="button" class="close" @click="closeMapModal">&times;</button>
                </div>
                <div class="modal-body">
                    <MapboxMap :initialBBOX="initialBBOX" @update-bbox="updateBbox" />
                </div>
                <div class="modal-footer">
                    <button class="btn btn-primary" @click="closeMapModal">Done</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue';
import MapboxMap from './MapComponent.vue';
import Datepicker from '@vuepic/vue-datepicker';
import Plotly from 'plotly.js-dist';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    name: 'EnsembleAnalysisTool',
    components: {
        MapboxMap,
        Datepicker,
    },
    setup() {
        const parsedIds = ref([]);
        const parameterNames = ref([]);
        const schemaKeys = ref([]);
        const initialBBOX = ref([]);
        const currentBbox = ref(null);
        const showMapModal = ref(false);

        const selectedRcpScenarios = ref([]);
        const selectedParameter = ref('temperature_mean');
        const selectedStartDate = ref(new Date().getFullYear());
        const selectedEndDate = ref(new Date().getFullYear() + 30);
        const selectedTemporalAggregation = ref('ANNUAL');

        const isLoading = ref(false);
        const ensemblePlotData = ref({});

        const getColorForScenario = (scenario) => {
            const s = scenario.toUpperCase();
            if (s.includes('26') || s.includes('2.6')) return 'rgb(0,100,80)';
            if (s.includes('45') || s.includes('4.5')) return 'rgb(255,127,0)';
            if (s.includes('85') || s.includes('8.5')) return 'rgb(227,26,28)';
            if (s.includes('60') || s.includes('6.0')) return 'rgb(200, 180, 0)';
            return 'rgb(128,128,128)'; // Fallback color
        };

        const getFillColorForScenario = (scenario) => {
            const s = scenario.toUpperCase();
            if (s.includes('26') || s.includes('2.6')) return 'rgba(0,100,80,0.2)';
            if (s.includes('45') || s.includes('4.5')) return 'rgba(255,127,0,0.2)';
            if (s.includes('85') || s.includes('8.5')) return 'rgba(227,26,28,0.2)';
            if (s.includes('60') || s.includes('6.0')) return 'rgba(200, 180, 0,0.2)';
            return 'rgba(128,128,128,0.2)'; // Fallback fill color
        };

        const fetchAndProcessIds = async () => {
            try {
                const response = await fetch('/api/collections');
                const data = await response.json();
                if (data.collections && data.collections.length > 0) {
                    parsedIds.value = data.collections
                        .map(c => parseId(c.id))
                        .filter(id => id !== null);

                    const referenceCollection = data.collections.find(c => c.id.startsWith('MDK_SN_SA_TH'));
                    if (referenceCollection) {
                        try {
                            const schemaResponse = await fetch(`/api/collections/${encodeURIComponent(referenceCollection.id)}`);
                            const schema = await schemaResponse.json();

                            if (schema.parameter_names) {
                                parameterNames.value = Object.keys(schema.parameter_names);
                                schemaKeys.value = Object.entries(schema.parameter_names);
                            } else {
                                console.warn('parameter_names missing in cached schema for', referenceCollection.id);
                            }

                            if (schema.extent?.spatial?.bbox?.[0] && Array.isArray(schema.extent.spatial.bbox[0])) {
                                initialBBOX.value = schema.extent.spatial.bbox[0];
                            } else if (referenceCollection.extent?.spatial?.bbox?.[0]) {
                                initialBBOX.value = referenceCollection.extent.spatial.bbox[0];
                            }
                        } catch (err) {
                            console.error('Error fetching cached collection schema:', err);
                        }
                    } else {
                         console.error('No collection starting with "MDK_SN_SA_TH" found to determine initial BBOX and schema.');
                    }
                }
            } catch (error) {
                console.error('Error fetching or processing IDs:', error);
            }
        };

        const parseId = (id) => {
            if (id.includes('KlimaKonform')) {
                return null;
            }
            if (!id.startsWith('MDK_SN_SA_TH_')) {
                return null;
            }
            const parts = id.split('_');
            if (parts.length >= 8) {
                const globalModel = parts[5];
                const regionalModel = parts[6];
                const rcpScenario = parts[7];
                return { id, globalModel, regionalModel, rcpScenario };
            }
            return null;
        };

        const availableRcpScenarios = computed(() => {
            return [...new Set(parsedIds.value.map(id => id.rcpScenario))].sort();
        });

        const allScenariosSelected = computed(() => {
            return availableRcpScenarios.value.length > 0 && selectedRcpScenarios.value.length === availableRcpScenarios.value.length;
        });

        const toggleSelectAllScenarios = () => {
            if (allScenariosSelected.value) {
                selectedRcpScenarios.value = [];
            } else {
                selectedRcpScenarios.value = [...availableRcpScenarios.value];
            }
        };

        const fetchEnsembleData = async () => {
            if (selectedRcpScenarios.value.length === 0) {
                alert('Please select at least one RCP scenario.');
                return;
            }
            isLoading.value = true;
            ensemblePlotData.value = {};

            const getYear = (date) => (date instanceof Date ? date.getFullYear() : date);

            const promises = selectedRcpScenarios.value.map(async (scenario) => {
                const collection_ids = parsedIds.value
                    .filter(id => id.rcpScenario === scenario)
                    .map(id => id.id);

                const postData = {
                    collection_ids: collection_ids,
                    climate_indicator: selectedParameter.value,
                    start_year: getYear(selectedStartDate.value).toString(),
                    end_year: getYear(selectedEndDate.value).toString(),
                    bbox: currentBbox.value || initialBBOX.value,
                    temporal_resolution: selectedTemporalAggregation.value
                };

                try {
                    const response = await fetch('/api/explorer-data', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(postData)
                    });
                    const result = await response.json();
                    if (!response.ok) {
                        throw new Error(result.message || `Failed to fetch data for ${scenario}.`);
                    }
                    return { scenario, data: result };
                } catch (error) {
                    console.error(`Error fetching data for ${scenario}:`, error);
                    return { scenario, error };
                }
            });

            const results = await Promise.all(promises);

            const newPlotData = {};
            results.forEach(result => {
                if (result.data) {
                    const processed = processEnsembleData(result.data);
                    if (processed) {
                        newPlotData[result.scenario] = processed;
                    }
                }
            });

            ensemblePlotData.value = newPlotData;

            if (Object.keys(ensemblePlotData.value).length > 0) {
                nextTick(() => {
                    plotEnsembleChart();
                });
            }
            isLoading.value = false;
        };

        const processEnsembleData = (data) => {
            const modelDataArrays = Object.values(data.value);
            const modelCount = modelDataArrays.length;
            if (modelCount === 0) return null;

            const firstModel = modelDataArrays[0];
            const numPoints = firstModel.values.length;
            const dates = getDatesFromMetadata(firstModel.metadata, numPoints);

            const q1 = [];
            const median = [];
            const q3 = [];

            for (let i = 0; i < numPoints; i++) {
                const valuesAtTimeStep = modelDataArrays.map(model => model.values[i]).filter(v => v !== null && !isNaN(v));
                if (valuesAtTimeStep.length > 0) {
                    valuesAtTimeStep.sort((a, b) => a - b);
                    median.push(getPercentile(valuesAtTimeStep, 50));
                    q1.push(getPercentile(valuesAtTimeStep, 25));
                    q3.push(getPercentile(valuesAtTimeStep, 75));
                } else {
                    median.push(null);
                    q1.push(null);
                    q3.push(null);
                }
            }
            return { dates, q1, median, q3, modelCount };
        };

        const getDatesFromMetadata = (metadata, numPoints) => {
            if (metadata && metadata.datetime) {
                const [startStr, endStr] = metadata.datetime.split('/');
                const startDate = new Date(startStr);
                const endDate = new Date(endStr);
                const timeDiff = endDate - startDate;
                const avgStep = numPoints > 1 ? timeDiff / (numPoints - 1) : 0;
                let dates = [];
                for (let i = 0; i < numPoints; i++) {
                    const date = new Date(startDate.getTime() + i * avgStep);
                    dates.push(date.toISOString().split('T')[0]);
                }
                return dates;
            }
            return [];
        };

        const getPercentile = (sortedArr, percentile) => {
            const index = (percentile / 100) * (sortedArr.length - 1);
            if (index % 1 === 0) {
                return sortedArr[index];
            }
            const lower = Math.floor(index);
            const upper = lower + 1;
            const weight = index - lower;
            return sortedArr[lower] * (1 - weight) + sortedArr[upper] * weight;
        };

        const plotEnsembleChart = () => {
            const plotData = ensemblePlotData.value;
            if (Object.keys(plotData).length === 0) return;

            const keys = schemaKeys.value;
            const paramInfo = keys.find(key => key[0] === selectedParameter.value);
            const yAxisTitle = paramInfo ? `${paramInfo[1].name} [${paramInfo[1].unit.symbol.value}]` : selectedParameter.value;

            const traces = [];
            const scenarios = Object.keys(plotData).sort();

            scenarios.forEach((scenario) => {
                const { dates, q1, median, q3, modelCount } = plotData[scenario];
                const color = getColorForScenario(scenario);
                const fillColor = getFillColorForScenario(scenario);

                traces.push({
                    x: dates,
                    y: q1,
                    fill: 'none',
                    mode: 'lines',
                    line: { color: 'transparent' },
                    name: `${scenario} 25th Percentile`,
                    legendgroup: scenario,
                    showlegend: false,
                });
                traces.push({
                    x: dates,
                    y: q3,
                    fill: 'tonexty',
                    mode: 'lines',
                    line: { color: 'transparent' },
                    fillcolor: fillColor,
                    name: `${scenario} 75th Percentile`,
                    legendgroup: scenario,
                    showlegend: false,
                });
                traces.push({
                    x: dates,
                    y: median,
                    mode: 'lines',
                    line: { color: color },
                    name: `${scenario} (${modelCount} models)`,
                    legendgroup: scenario,
                });
            });

            const layout = {
                title: `Ensemble Projection Comparison: ${selectedParameter.value}`,
                xaxis: { title: 'Date', type: 'date' },
                yaxis: { title: yAxisTitle },
                legend: { orientation: 'v' },
                showlegend: true,
            };

            Plotly.newPlot('ensembleChart', traces, layout);
        };

        const updateBbox = (bbox) => {
            currentBbox.value = bbox;
        };

        const closeMapModal = () => {
            showMapModal.value = false;
        };

        onMounted(() => {
            fetchAndProcessIds();
        });

        return {
            selectedRcpScenarios,
            availableRcpScenarios,
            allScenariosSelected,
            toggleSelectAllScenarios,
            selectedParameter,
            parameterNames,
            selectedStartDate,
            selectedEndDate,
            selectedTemporalAggregation,
            initialBBOX,
            showMapModal,
            isLoading,
            ensemblePlotData,
            fetchEnsembleData,
            updateBbox,
            closeMapModal,
        };
    }
};
</script>

<style scoped>
.sidebar {
    border-radius: 25px;
}
.info-panel {
    border-radius: 25px;
}
.loading-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
}
.spinner {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
}
@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
}
.modal-content {
    background-color: white;
    padding: 20px;
    width: 80%;
    max-width: 800px;
    border-radius: 8px;
}
.placeholder-container {
    text-align: center;
    padding: 50px;
    border: 1px dashed #ccc;
    border-radius: 10px;
    margin-top: 20px;
}
.placeholder-text {
    color: #888;
    font-style: italic;
}
</style>